import React, { useEffect, useRef, useState } from "react";
import { Button, Groupper, Header, Input, Message, Checkbox, Modal, useFrontier } from 'react-frontier2';
import { bindClick, bindFormChange, fromUnix, getNow } from "@arema/components/Util";
import { ShortUser, SetLoading } from "@arema/components/Classes";
import { EventSectionProps } from '../../AdminClasses';
import { SearchUserModal, EventPaymentPlanModal, SearchBankModal, CheckboxAsync } from '../../components';
import { useUser } from "../../AdminHooks";
import { BankBin } from "../../AdminClasses";
import { Link } from "react-router-dom";
import { CDN_URL } from "../../AdminConfig";
import { useTranslation } from "react-i18next";
import API from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from '../../UserAccess';

enum MODAL {
	ADMIN_MODAL = 101,
	EDIT_PAYMENT_PLAN = 102,
	BANK_MODAL = 103,
	CONFIRM_DELETE_BANK = 104,
	DELETE_POSTER = 105,
}

var EventContainer = (props: EventSectionProps)=>{
	var { t } = useTranslation();
	var { hasAccess, user } = useUser();
	var { Toast } = useFrontier();
	var mapFileRef = useRef<HTMLInputElement>(null);
	var [options, setOptions] = useState({ ...props.event });
	var [submitPrompts, setSubmitPrompts] = useState<string[]>(null)
	var [shownModal, setShownModal] = useState<MODAL>(null);

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(options, {
			subdomain: [
				{ rule: 'length', params: [3, 16], prompt: 'El subdominio debe ser entre 3 y 16 caracteres.', skipEmpty: true },
				{ rule: /^(?<s>[a-z0-9]{3,32})$/gi, prompt: 'El subdominio no es válido', skipEmpty: true },
			],
			access_code: [
				{ rule: 'minLength', params: [3], prompt: 'El código de acceso debe ser entre 3 y 16 caracteres.', skipEmpty: true },
				{ rule: 'maxLength', params: [32], prompt: 'El código de acceso debe ser entre 3 y 32 caracteres.', skipEmpty: true },
			],
			/* commission_return: [
				{ rule: 'number', prompt: 'El retorno debe ser un numero mayor a cero.', skipEmpty: true },
				{ rule: 'min', params: [0] }
			], */
		});
		setSubmitPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		const commission_return = (options.commission_return === null || `${options.commission_return}`.trim() === '') ? null : (options.commission_return / 100);
		API.setEventOptions(props.event.event_id, {
			subdomain: options.subdomain,
			access_code: options.access_code,
			published: options.published,
			meta_pixel: options.meta_pixel,
			google_tag: options.google_tag,
			tiktok_pixel: options.tiktok_pixel,
			date_publish: options.date_publish,
			dynamic_code: !!options.dynamic_code,
			commission_return: commission_return,
			date_start: options.date_start,
		}).then(res=>{
			if(res.error) return setSubmitPrompts([res.message]);
			Toast.success('Se han guardado las opciones del evento');
			props.onEditEvent(options);
		}).catch(err=>{
			return setSubmitPrompts(['Hubo un error inesperado guardando las opciones del evento (LCL-EOSBMT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onAdminChange = (admin: ShortUser, setLoading: SetLoading)=>{
		if(admin.user_id !== props.event.user_assigned) {
			setLoading(true);
			API.setEventUserAssigned(props.event.event_id, admin.user_id).then(res=>{
				if(res.error) return Toast.error(res.message);
				Toast.success('Se asigno correctamente el usuario');
				setOptions({
					...props.event,
					user_assigned: admin.user_id,
					user_assigned_name: `${admin.first_name} ${admin.last_name}`,
				});
				setShownModal(null);
			}).catch(err=>{
				Toast.success('Error inesperado (LCL-EOADMCHG-1)');
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			Toast.error('El usuario seleccionado ya es el responsable actual del evento');
		}
	}

	var onBankChange = (bank: BankBin, setLoading: SetLoading)=>{
		if(props.event.bank_id === bank.bank_id) return Toast.error('El banco ya se encuentra seleccionado')
		setLoading(true);
		API.setEventBank(props.event.event_id, bank.bank_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se asigno correctamente el banco');
			setOptions({
				...props.event,
				bank_id: bank.bank_id,
				bank_name: bank.bank_name,
			});
			setShownModal(null);
		}).catch(err=>{
			Toast.success('Error inesperado (LCL-EOBNKCHG-2)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onBankRemove = (setLoading: SetLoading)=>{
		setLoading(true);
		API.setEventBank(props.event.event_id, null).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se eliminó el banco de este evento');
			setOptions({
				...props.event,
				bank_id: null,
				bank_name: '',
			});
			setShownModal(null);
		}).catch(err=>{
			Toast.success('Error inesperado (LCL-EOBKRM-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onMapFileChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
		var files = event.target.files;
		if(!files || !files[0]) return;
		if(files.length>1) return Toast.error(t('toasts.one_file'));

		var file_size = (2 * 1024 * 1024);
		if(files[0].size > file_size) return Toast.error(t('toasts.file_size', { size: `${Math.floor(file_size / 1024 / 1024)}MB` }));

		var tx = Toast.show('Subiendo imágen', {
			loading: true,
			dismissable: false
		});
		API.uploadEventMap(props.event.event_id, files[0]).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success(t('toasts.asset_uploaded'));
			setOptions(o=>({
				...o,
				has_map: true
			}));
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-UPLEVMP-1' }));
		}).finally(()=>{
			Toast.dismiss(tx);
		});
	}

	var onRemoveMap = ()=>{
		var tx = Toast.show('Subiendo imágen', {
			loading: true,
			dismissable: false
		});
		API.removeEventMap(props.event.event_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success(t('toasts.asset_deleted'));
			setOptions(o=>({
				...o,
				has_map: false
			}));
		}).catch(e=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-EVMPRM-1' }));
		}).finally(()=>{
			Toast.dismiss(tx);
		})
	}

	var onDeletePoster = (setLoading: SetLoading)=>{
		setLoading(true);
		API.removeEventPoster(props.event.event_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success(t('toasts.asset_deleted'));
			setShownModal(null);
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-EVPSTRM-1' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onFormChange = bindFormChange(options, setOptions)

	return <>
		<Groupper title="Opciones" style={{ maxWidth: 600, margin: 'auto' }} actions={(
			<Button text="Guardar" color="black" onClick={submit} />
		)}>
			{hasAccess(UserAccess.EVENTS.PUBLISH_EVENT) && (
				<div className="fr option">
					<div className="info">
						<div className="title">Publicar</div>
						<div className="description">Permitir acceso y venta del evento</div>
					</div>
					<Checkbox checked={!!options.published} onChange={onFormChange('published')} />
				</div>
			)}
			<div className="fr option">
				<div className="info">
					<div className="title">Evento secreto</div>
					<div className="description">Solamente podrá ser accesado mediante al enlace directo</div>
				</div>
				<CheckboxAsync checked={!!options.secret} onChange={onFormChange('secret')} onChecked={async v=>{
					try{
						var res = await API.setEventSecret(props.event.event_id, v);
						if(res.error){
							Toast.error(res.message);
							return false;
						}
						Toast.success(t('common.changes_saved'));
						props.onEditEvent({
							...options,
							secret: v
						});
						return true;
					}catch(e){
						return false;
					}
				}} />
				{/* <Checkbox toggle checked={!!options.secret} onChange={onFormChange('secret', true)} /> */}
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Fecha de publicación</div>
					<div className="description">El evento no se mostrará hasta esta fecha</div>
				</div>
				<Input 
					placeholder="Fecha de publicación"
					calendar={{ 
						date: options.date_publish ? fromUnix(options.date_publish).toJSDate() : null, 
						onSelected: v=>onFormChange('date_publish')(v.getTime()/1000),
						mode: 'datetime' 
					}}
					buttons={!options.date_publish ? null : (
						<Button icon
							iconName="remove" 
							style={{ minWidth: 0, paddingRight: 20 }} 
							iconStyle={{ margin: 0 }} 
							onClick={()=>onFormChange('date_publish')(null) } 
						/>)
					}
				/>
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Retorno</div>
					<div className="description">Retorno del promotor del evento</div>
				</div>
				<Input placeholder="Retorno" value={options.commission_return} onChange={onFormChange('commission_return')} />
			</div>
			{hasAccess(UserAccess.EVENTS.EDIT_SUBDOMAIN) && (
				<div className="fr option">
					<div className="info">
						<div className="title">Subdominio</div>
						<div className="description">Accesa al evento mediante el subdominio</div>
					</div>
					<Input placeholder="Subdominio" value={options.subdomain} onChange={onFormChange('subdomain')} />
				</div>
			)}
			<div className="fr option">
				<div className="info">
					<div className="title">Imágen del recinto</div>
					<div className="description">Imágen de referencia del recinto que se mostrará en la selección de butacas</div>
				</div>
				{hasAccess(UserAccess.EVENTS.EDIT_POSTER) ? <>
					<input type="file" style={{ display: 'none' }} ref={mapFileRef} accept="image/*" onChange={onMapFileChange} />
					{options.has_map ? (
						<div className="fr buttons merged">
							<Button text="Editar" iconName="pen" onClick={()=>{
								if(!mapFileRef.current) return;
								mapFileRef.current.click();
							}} />
							<Button iconName="remove" icon onClick={onRemoveMap} />
							<Button iconName="eye" icon as={Link} target="_blank" to={`${CDN_URL}/events/${props.event.event_id}/map.webp`} />
						</div>
					) : (
						<Button text="Editar" iconName="pen" onClick={()=>{
							if(!mapFileRef.current) return;
							mapFileRef.current.click();
						}} />
					)}
				</> : (
					<Button text="Ver" disabled />
				)}
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Tiktok pixel</div>
					<div className="description">Solo el identificador del pixel. Debe de estar en la sección que dice: ttq.load('IDENTIFICADOR DEL PIXEL') 
						{/* <div>Si son multiples pixeles, separarlos con un ;</div> */}
					</div>
				</div>
				<Input placeholder="Pixel" value={options.tiktok_pixel} onChange={onFormChange('tiktok_pixel')} />
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Meta pixel</div>
					<div className="description">Solo el identificador del pixel. Debe de estar en la sección que dice: fbq('init', 'IDENTIFICADOR DEL PIXEL')</div>
				</div>
				<Input placeholder="Pixel" value={options.meta_pixel} onChange={onFormChange('meta_pixel')} />
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Google Tag</div>
					<div className="description">Código de Google Tag Manager completo</div>
				</div>
				<Input placeholder="Google Tag" value={options.google_tag} onChange={onFormChange('google_tag')} />
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Cuenta Regresiva</div>
					<div className="description">Muestra un contador de fecha de lanzamiento del evento</div>
				</div>
				<Input calendar={{
					date: options.date_start ? fromUnix(options.date_start).toJSDate() : null,
					minDate: getNow().toJSDate(),
					mode: 'datetime',
					onSelected: v=>onFormChange('date_start')(v.getTime()/1000)
				}} placeholder="Cuenta Regresiva" />
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Responsable del evento</div>
					<div className="description">Usuario responsable del evento</div>
				</div>
				<Input placeholder="Responsable" readonly value={options.user_assigned_name} buttons={(
					(hasAccess(UserAccess.EVENTS.EDIT_USER_ASSIGNED) || user.user_id === options.user_assigned) ? (
						<Button style={{ minWidth: 0, paddingRight: 20 }} icon iconStyle={{ margin: 0 }} iconName="pen" onClick={bindClick(setShownModal, MODAL.ADMIN_MODAL)} />
					) : null
				)} />
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Código de acceso</div>
					<div className="description">Limitar el acceso para que requiera un código para entrar al evento</div>
				</div>
				<Input 
					placeholder="Código de acceso" 
					readonly={options.dynamic_code}
					value={options.dynamic_code ? 'Códigos' : options.access_code}
					inputStyle={options.dynamic_code ? { textAlign: 'center', fontWeight: 'bold' } : null}
					onChange={onFormChange('access_code')} 
					buttons={(
						<Button iconName="cog" icon menu={[
							{ text: 'Texto', iconName: !options.dynamic_code ? 'circle-check' : 'circle', iconSolid: !options.dynamic_code, onClick: ()=>onFormChange('dynamic_code')(false) },
							{ text: 'Códigos', iconName: !!options.dynamic_code ? 'circle-check' : 'circle', iconSolid: !!options.dynamic_code, onClick: ()=>onFormChange('dynamic_code')(true) }
						]} />
					)} 
				/>
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Banco Exclusivo</div>
					<div className="description">Banco exclusivo del evento</div>
				</div>
				<Input readonly
					placeholder="Banco"
					value={options.bank_name?.toUpperCase()}
					buttons={hasAccess(UserAccess.EVENTS.EDIT_EXCLUSIVE_BANK) ? (
						options.bank_id === null ? (
							<Button style={{ minWidth: 0, paddingRight: 20 }} icon iconStyle={{ margin: 0 }} iconName="pen" onClick={bindClick(setShownModal, MODAL.BANK_MODAL)} />
						) : (
							<Button style={{ minWidth: 0, paddingRight: 20 }} icon iconStyle={{ margin: 0 }} iconName="remove" onClick={bindClick(setShownModal, MODAL.CONFIRM_DELETE_BANK)} />
						)
					) : null}
				/>
			</div>
			<div className="fr option">
				<div className="info">
					<div className="title">Planes de Pago</div>
					<div className="description">Meses sin y con intereses</div>
				</div>
				{hasAccess(UserAccess.EVENTS.EDIT_OPTIONS_MSI) ? (
					<Button text="Editar" iconName="pen" onClick={bindClick(setShownModal, MODAL.EDIT_PAYMENT_PLAN)} />
				) : (
					<Button text="Editar" disabled />
				)}
			</div>
			<div className="fr last option">
				<div className="info" style={{ marginRight: 15 }}>
					<div className="title">Eliminar poster</div>
					<div className="description">Se eliminará la imagen de poster del evento. El eliminado puede tomar unos minutos en propagarse</div>
				</div>
				<Button text="Eliminar" onClick={bindClick(setShownModal, MODAL.DELETE_POSTER)} />
			</div>
			<Message list={submitPrompts} type="error" style={{ marginTop: 15 }} />
		</Groupper>

		<EventPaymentPlanModal
			open={shownModal === MODAL.EDIT_PAYMENT_PLAN}
			onClose={bindClick(setShownModal)}
			title="Editar planes de pago"
			eventId={props.event.event_id}
		/>

		<SearchUserModal
			open={shownModal === MODAL.ADMIN_MODAL}
			onClose={bindClick(setShownModal)}
			onSelected={onAdminChange}
		/>

		<SearchBankModal
			open={shownModal === MODAL.BANK_MODAL}
			onClose={bindClick(setShownModal)}
			onSelected={onBankChange}
		/>

		<Modal open={shownModal === MODAL.CONFIRM_DELETE_BANK} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>Eliminar el banco</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar Banco?' style={{ marginBottom: 20 }} />
				<Message type='info' centered>
					Se eliminará el banco de este evento
				</Message>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Continuar' color='black' onClick={onBankRemove} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal === MODAL.DELETE_POSTER} size='mini' onClose={bindClick(setShownModal)}>
			<Modal.Header>Eliminar poster</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar poster?' style={{ marginBottom: 20 }} />
				<Message type='info' centered>
					Se eliminará la imágen de poster del evento. <b>El eliminado completo puede tardarse unos minutos en propagarse</b>
				</Message>
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.delete')} color='red' onClick={onDeletePoster} />
			</Modal.Actions>
		</Modal>
	</>
}

export default EventContainer;